import { FC, SyntheticEvent, useMemo, useState, useCallback, useEffect } from "react";
import { Autocomplete, Box, FilterOptionsState, MenuItem, TextField, css, styled } from "@mui/material";
import { background, blue, button, divider, text } from "@/theme/colors";
import { typography } from "@/theme/typography";
import { rounded } from "@/theme/variables";
import { TOption } from "@/types/common";
import { convertFullWidthToHalfWidth } from "@/utils/pieces";

type FreeInputComboboxProps = {
  placeholder?: string;
  value?: string;
  options: TOption[];
  onChange?: (value: string) => void;
};

export const FreeInputCombobox: FC<FreeInputComboboxProps> = ({ placeholder = "選択してください", value = "", options, onChange }) => {
  const [inputValue, setInputValue] = useState("");

  // Sync inputValue when external value is cleared
  useEffect(() => {
    if (!value) {
      setInputValue("");
    }
  }, [value]);

  const autocompleteValue = useMemo(() => {
    if (!value) return null;
    return options.find((opt) => String(opt.value) === String(value)) ?? value;
  }, [options, value]);

  const handleChange = useCallback(
    (_: SyntheticEvent, newValue: unknown) => {
      if (newValue === null) {
        onChange?.("");
      } else if (typeof newValue === "string") {
        onChange?.(newValue);
      } else {
        onChange?.((newValue as TOption).value as string);
      }
    },
    [onChange],
  );

  const handleInputChange = useCallback(
    (_: SyntheticEvent, newInputValue: string, reason: string) => {
      setInputValue(newInputValue);
      if (reason === "input") {
        onChange?.(newInputValue);
      } else if (reason === "clear") {
        onChange?.("");
      }
    },
    [onChange],
  );

  const filterOptions = useCallback((opts: unknown[], state: FilterOptionsState<unknown>) => {
    if (state.inputValue === "") return opts;
    return opts.filter((opt) => {
      const label = convertFullWidthToHalfWidth((opt as TOption).label);
      const query = convertFullWidthToHalfWidth(state.inputValue);
      return label.includes(query) || label.toLowerCase().includes(query.toLowerCase());
    });
  }, []);

  return (
    <BoxWrapper>
      <StyledAutocomplete
        freeSolo
        noOptionsText="選択肢なし"
        options={options}
        value={autocompleteValue}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onChange={handleChange}
        getOptionLabel={(option) => (typeof option === "string" ? option : (option as TOption).label)}
        filterOptions={filterOptions}
        renderInput={(params) => <TextField placeholder={placeholder} {...params} />}
        renderOption={(props, option) => {
          const opt = option as TOption;
          if (opt.hidden === true && value !== String(opt.value)) return null;
          return (
            <MenuItem {...props} key={opt.value} value={opt.value} title={opt.label}>
              <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{opt.label}</div>
            </MenuItem>
          );
        }}
        componentsProps={{
          paper: {
            sx: {
              fontSize: "14px !important",
              ".MuiMenuItem-root": typography.body14,
            },
          },
        }}
      />
    </BoxWrapper>
  );
};

const BoxWrapper = styled(Box)``;

const StyledAutocomplete = styled(Autocomplete)`
  width: 100%;
  .MuiAutocomplete-inputRoot {
    padding: 0px !important;
  }
  .MuiInputBase-root {
    position: relative;
    background: ${background.white};
    .MuiSelect-select {
      color: ${text.primary};
    }
    input::placeholder {
      color: ${text.primary};
    }
    fieldset {
      border-color: ${divider.middle};
      border-radius: ${rounded.xs};
      border-width: 1px !important;
    }
    &:hover,
    &.Mui-focused {
      fieldset {
        border-color: ${blue[70]};
      }
    }
    &.Mui-focused {
      &::after {
        content: "";
        position: absolute;
        top: -4px;
        left: -4px;
        width: 100%;
        height: 100%;
        padding: 2px;
        border: 2px solid ${button.secondary};
        box-sizing: content-box;
        border-radius: ${rounded.sm};
        z-index: -1;
      }
    }
    .MuiInputBase-input {
      padding: 6px 8px;
      ${css(typography.body14)};
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
    }
  }
`;
